// Ticket lifecycle (docs/ticket-lifecycle.md).
//
// Scanning is never a ticket status. Admission is a separate fact
// (admission_scans + ticket_admission_state). Transfers and reissues keep the
// ticket's identity and bump its credential version.
import type { Permission, ScanResult, TicketStatus } from '@bre-ticketing/contracts';

export interface TicketTransition {
  readonly from: TicketStatus;
  readonly to: TicketStatus;
  readonly requiredPermission: Permission | null;
}

const transition = (from: TicketStatus, to: TicketStatus, requiredPermission: Permission | null = null): TicketTransition => ({
  from,
  to,
  requiredPermission,
});

export const TICKET_TRANSITIONS: readonly TicketTransition[] = Object.freeze([
  transition('ISSUED', 'TRANSFER_PENDING'),
  transition('ISSUED', 'TRANSFERRED', 'transfers.admin'),
  transition('ISSUED', 'REISSUED', 'tickets.reissue'),
  transition('ISSUED', 'REFUNDED'),
  transition('ISSUED', 'VOIDED', 'tickets.void'),
  transition('ISSUED', 'REVOKED', 'tickets.revoke'),

  // Cancel/decline/expire restores pre_transfer_status; accept -> TRANSFERRED.
  transition('TRANSFER_PENDING', 'ISSUED'),
  transition('TRANSFER_PENDING', 'TRANSFERRED'),
  transition('TRANSFER_PENDING', 'REISSUED'),
  transition('TRANSFER_PENDING', 'REFUNDED'),
  transition('TRANSFER_PENDING', 'VOIDED', 'tickets.void'),
  transition('TRANSFER_PENDING', 'REVOKED', 'tickets.revoke'),

  transition('TRANSFERRED', 'TRANSFER_PENDING'),
  transition('TRANSFERRED', 'REISSUED', 'tickets.reissue'),
  transition('TRANSFERRED', 'REFUNDED'),
  transition('TRANSFERRED', 'VOIDED', 'tickets.void'),
  transition('TRANSFERRED', 'REVOKED', 'tickets.revoke'),

  // A ticket on an unpaid order (D-71): it issues when the order is paid, or goes away with it.
  transition('PENDING_PAYMENT', 'ISSUED'),
  transition('PENDING_PAYMENT', 'VOIDED'),
  transition('PENDING_PAYMENT', 'REFUNDED'),

  transition('REISSUED', 'TRANSFER_PENDING'),
  transition('REISSUED', 'TRANSFERRED', 'transfers.admin'),
  transition('REISSUED', 'REFUNDED'),
  transition('REISSUED', 'VOIDED', 'tickets.void'),
  transition('REISSUED', 'REVOKED', 'tickets.revoke'),

  // Revocation (fraud, lost chargeback) can be reversed, always with a new credential.
  transition('REVOKED', 'REISSUED', 'tickets.reinstate'),
  transition('REVOKED', 'REFUNDED'),
  transition('REVOKED', 'VOIDED', 'tickets.void'),
]);

export const ADMISSIBLE_TICKET_STATUSES: readonly TicketStatus[] = Object.freeze(['ISSUED', 'TRANSFERRED', 'REISSUED']);
export const TERMINAL_TICKET_STATUSES: readonly TicketStatus[] = Object.freeze(['REFUNDED', 'VOIDED']);

const transitionsByKey = new Map(TICKET_TRANSITIONS.map((t) => [`${t.from}->${t.to}`, t]));

export function findTicketTransition(from: TicketStatus, to: TicketStatus): TicketTransition | undefined {
  return transitionsByKey.get(`${from}->${to}`);
}

export function isAdmissible(status: TicketStatus): boolean {
  return ADMISSIBLE_TICKET_STATUSES.includes(status);
}

/**
 * The rejection a gate shows for a ticket's lifecycle state and presented
 * credential version, before event/performance/zone/duplicate checks.
 * Returns null when the lifecycle permits admission.
 */
export function lifecycleScanRejection(
  status: TicketStatus,
  currentCredentialVersion: number,
  presentedCredentialVersion: number,
): ScanResult | null {
  if (status === 'REFUNDED') return 'REFUNDED';
  if (status === 'REVOKED') return 'REVOKED';
  if (status === 'VOIDED') return 'INVALID';
  if (status === 'TRANSFER_PENDING') return 'INVALID';
  // Not yet paid (D-71): no credential exists, or the order's policy keeps admission off until it is paid.
  if (status === 'PENDING_PAYMENT') return 'INVALID';
  if (presentedCredentialVersion < currentCredentialVersion) return 'TRANSFERRED';
  if (presentedCredentialVersion > currentCredentialVersion) return 'INVALID';
  return null;
}
